import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, Store, Server, Users, Clock, Check, ArrowRight, Building, DollarSign } from "lucide-react";
import { getBusinessPage } from "../../data/businessPages.js";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const features = [
  {
    icon: DollarSign,
    title: "Affordable pricing",
    description: "Strong security at small business prices. No hidden fees, no per-user licensing complexity. Simple, predictable costs."
  },
  {
    icon: Lock,
    title: "Easy setup",
    description: "No IT department needed. Set up VPN protection for your whole team in under 10 minutes with guided onboarding."
  },
  {
    icon: Server,
    title: "Protect business data",
    description: "Secure customer data, financial records, and business communications with WireGuard's modern encryption."
  },
  {
    icon: Shield,
    title: "Cyber insurance ready",
    description: "Help meet cyber insurance requirements for network security. SACVPN provides documentation for your policy."
  },
  {
    icon: Users,
    title: "Grow with you",
    description: "Start with what you need and scale as you grow. Add devices instantly without contract renegotiation."
  },
  {
    icon: Store,
    title: "Multi-location support",
    description: "Connect multiple store locations, warehouses, or offices with secure site-to-site VPN tunnels."
  }
];

const useCases = [
  "Secure point-of-sale (POS) systems",
  "Remote employee access",
  "Customer data protection",
  "Business email security",
  "Cloud accounting access",
  "Inventory management systems",
  "Multi-location connectivity",
  "Vendor and supplier portals"
];

const whyChoose = [
  { title: "No IT required", desc: "Set up in minutes, not days" },
  { title: "Predictable cost", desc: "Simple per-device pricing" },
  { title: "US-based email support", desc: "Real help when you need it" },
  { title: "Risk-free trial", desc: "14 days free, no card required" }
];

// Cross-links to the specific /business/:slug vertical guides in src/data/businessPages.js.
// This page stays the general small-business overview; readers running one of these
// specific business types get routed to the guide written for them instead of splitting
// search/link equity between two competing generic-vs-specific pages. Slugs are grouped
// for scanability, but every slug is resolved through getBusinessPage() so a typo or a
// removed entry fails closed (renders nothing) instead of linking to a dead page.
const relatedVerticals = [
  {
    category: "Trades & field services",
    slugs: ["construction", "real-estate", "property-management", "architecture-firm", "engineering-firm", "logistics"],
  },
  {
    category: "Retail & commerce",
    slugs: ["retail", "ecommerce", "auto-dealership", "hospitality"],
  },
  {
    category: "Professional & financial services",
    slugs: ["accounting", "insurance", "staffing-agency", "it-msp", "marketing-agency"],
  },
  {
    category: "Health & wellness practices",
    slugs: ["dental", "medical-spa", "veterinary"],
  },
  {
    category: "Nonprofits & mission-driven orgs",
    slugs: ["nonprofits", "nonprofit-foundation"],
  },
];

export default function SmallBusinessVPN() {
  return (
    <>
      <Helmet>
        <title>VPN for Small Business | Affordable Business VPN Security | SACVPN</title>
        <meta
          name="description"
          content="Affordable VPN security for small businesses. Easy setup, no IT required. Protect customer data, secure remote workers, and help meet cyber insurance requirements."
        />
        <meta
          name="keywords"
          content="small business VPN, affordable business VPN, SMB VPN, business network security, small company VPN, startup VPN, retail VPN, POS security"
        />
        <link rel="canonical" href="https://www.sacvpn.com/industries/small-business" />
        <meta property="og:title" content="VPN for Small Business | Affordable Business VPN Security | SACVPN" />
        <meta property="og:description" content="Affordable VPN security for small businesses. Easy setup, no IT required. Protect customer data, secure remote workers, and help meet cyber insurance requirements." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Small Business VPN Service",
            "name": "SACVPN Small Business VPN Solutions",
            "description": "Affordable, easy-to-use VPN service designed for small businesses and startups.",
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Small Businesses and Startups"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Small business
              </motion.p>

              <motion.h1
                variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
              >
                VPN security for{" "}
                <span className="text-brand-400">small business</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-lg text-gray-400 leading-relaxed max-w-xl"
              >
                Strong protection at prices small businesses can afford. No IT department needed, no complex setup. Just security that works.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  <Clock className="w-5 h-5" />
                  Start free trial
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                >
                  Request demo
                </Link>
              </motion.div>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-sm text-gray-500"
              >
                Business plans start at $100/month for up to 10 devices, as low as $55/month with a 3-year term. No contracts on month-to-month billing, cancel anytime.
              </motion.p>
            </div>

            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Store className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Small business ready</h3>
                    <p className="text-gray-500 text-sm">Security made simple</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {["No IT expertise required", "10-minute setup", "Unlimited devices option", "US-based email support"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-gray-400">
                      <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Why choose us</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Built for businesses like yours
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              We understand small business needs: simple, affordable, and effective.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {whyChoose.map((point, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <Check className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Everything your business needs
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden"
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Use cases
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-6"
              >
                Small business use cases
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-lg text-gray-400 mb-8 leading-relaxed"
              >
                From retail stores to professional services, SACVPN protects businesses of all types.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                {useCases.map((useCase, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{useCase}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8"
            >
              <h3 className="text-xl font-semibold text-white mb-6">Simple, transparent pricing</h3>
              <div className="space-y-4">
                {[
                  "Personal plan: unlimited devices for individuals",
                  "Business 10: perfect for teams of 1-10",
                  "Business 50: growing businesses",
                  "Enterprise: custom solutions for larger teams",
                  "All plans include a 14-day free trial"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-400">
                    <ArrowRight className="w-5 h-5 text-brand-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 mt-6 text-brand-400 font-medium hover:text-brand-300 transition-colors"
              >
                View all pricing options <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">At a glance</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Simple by design
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { v: "< 60s", l: "To connect a device" },
              { v: "WireGuard", l: "Modern protocol" },
              { v: "US-based", l: "Email support" },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
                <div className="text-3xl font-display font-bold text-white tracking-tight">{s.v}</div>
                <div className="text-gray-500 mt-1.5 text-sm">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Find your specific business type - cross-links to the dedicated /business/:slug guides */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Find your fit</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Run a specific type of small business?
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              This page covers small business VPN basics for any team. If yours fits one of the categories below, see our dedicated guide for it instead. It covers the exact risks, workflows, and setup notes for your industry.
            </p>
          </motion.div>

          <div className="space-y-12">
            {relatedVerticals.map((group) => (
              <motion.div
                key={group.category}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={stagger}
              >
                <motion.h3
                  variants={fadeUp}
                  className="text-xs font-mono uppercase tracking-[0.16em] text-gray-500 mb-4"
                >
                  {group.category}
                </motion.h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.slugs.map((slug) => {
                    const biz = getBusinessPage(slug);
                    if (!biz) return null;
                    return (
                      <motion.div key={biz.slug} variants={fadeUp}>
                        <Link
                          to={`/business/${biz.slug}`}
                          className="group flex flex-col h-full rounded-xl border border-white/[0.08] bg-dark-800/40 p-5 hover:border-brand-500/30 transition-colors"
                        >
                          <span className="font-semibold text-white group-hover:text-brand-400 transition-colors">
                            VPN for {biz.name}
                          </span>
                          <span className="mt-1 text-sm text-gray-500">{biz.tagline}</span>
                          <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-400 font-medium">
                            Read the guide <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Protect your business today
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Affordable, reliable security for small teams. Start your 14-day free trial, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Clock className="w-5 h-5" />
                Start free trial
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Contact sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
